import {
	COLLECTION_QUERY_KEY,
	type AttributeValue,
	type CollectionSelection,
	parseCollectionSelection,
} from "@lobu/core/contracts/tools/collection-selection";
import { schemaEnumLabel, visibleSchemaEntries } from "@/lib/schema-annotations";

/** Preserve malformed values too: the page must explain them, not widen scope. */
export function collectionRouteValue(raw: unknown): string | undefined {
	return raw === undefined ? undefined : typeof raw === "string" ? raw : JSON.stringify(raw);
}

export function readCollection(search: Record<string, unknown>, viewPath = false):
	{ collection: CollectionSelection; error?: Error } {
	try {
		const raw = search[COLLECTION_QUERY_KEY];
		if (raw !== undefined && !viewPath && typeof search.segment === "string" && search.segment) {
			throw new Error("Invalid collection selection: use either $collection or segment, not both");
		}
		return { collection: parseCollectionSelection(raw === undefined && !viewPath && typeof search.segment === "string"
			? { segment: search.segment } : raw) };
	} catch (error) {
		return { collection: {}, error: error instanceof Error ? error : new Error("Invalid collection selection") };
	}
}

/** Only host-owned selection crosses tab boundaries. */
export function collectionSearch(collection: CollectionSelection): Record<string, string> {
	const normalized = {
		...(collection.search ? { search: collection.search } : {}),
		...(collection.filters?.length ? { filters: collection.filters } : {}),
		...(collection.segment ? { segment: collection.segment } : {}),
	};
	return Object.keys(normalized).length ? { [COLLECTION_QUERY_KEY]: JSON.stringify(normalized) } : {};
}

interface AttributeFacet {
	field: string;
	label: string;
	sidebar: boolean;
	options: { value: AttributeValue; label: string }[];
}

export function attributeFacets(schema: unknown): AttributeFacet[] {
	if (!schema || typeof schema !== "object") return [];
	const properties = (schema as { properties?: Record<string, Record<string, unknown>> }).properties;
	return visibleSchemaEntries(properties).flatMap(([field, definition]) => {
		if (!definition || (definition["x-facet"] !== true && definition["x-sidebar"] !== true)) return [];
		const branches = [definition, ...(Array.isArray(definition.anyOf) ? definition.anyOf : [])];
		const values: AttributeValue[] = [];
		for (const branch of branches) {
			if (!branch || typeof branch !== "object") continue;
			const raw = Array.isArray(branch.enum) ? branch.enum : branch.type === "null" ? [null] : [];
			for (const value of raw) {
				if ((value === null || ["string", "number", "boolean"].includes(typeof value)) && !values.includes(value)) values.push(value);
			}
		}
		return values.length ? [{ field, label: String(definition["x-table-label"] ?? definition.title ?? field.replaceAll("_", " ")), sidebar: definition["x-sidebar"] === true,
			options: values.map(value => ({ value, label: schemaEnumLabel(definition, value) ?? (value === null ? "Not supplied" : String(value).replaceAll("_", " ")) })) }] : [];
	});
}
